import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

export type PlatformAdmin = {
  id: number;
  name: string;
  email: string;
  status: string;
  platform_role_id: number | null;
  last_login_at?: string | null;
  created_at?: string | null;
  role?: {
    id: number;
    name: string;
    slug: string;
    color: string;
    is_system: boolean;
  } | null;
  permissions?: string[];
  has_full_access?: boolean;
};

export async function fetchPlatformAdmins(params?: {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
}): Promise<
  | { ok: true; data: PlatformAdmin[]; meta: PaginationMeta }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const qs = new URLSearchParams();
  qs.set('page', String(params?.page ?? 1));
  qs.set('per_page', String(params?.per_page ?? 50));
  if (params?.status) qs.set('status', params.status);
  if (params?.search) qs.set('search', params.search);

  const res = await apiFetch(`/platform/admins?${qs.toString()}`);
  const out = await parseJsonResponse<PlatformAdmin[]>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return {
    ok: true,
    data: Array.isArray(out.data) ? out.data : [],
    meta: out.meta ?? { current_page: 1, per_page: 50, total: 0, last_page: 1 },
  };
}

export async function createPlatformAdmin(payload: {
  name: string;
  email: string;
  password: string;
  platform_role_id: number;
  status?: string;
}): Promise<{ ok: true; admin: PlatformAdmin } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch('/platform/admins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformAdmin>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, admin: out.data };
}

export async function updatePlatformAdmin(
  id: number,
  payload: {
    name?: string;
    email?: string;
    password?: string;
    platform_role_id?: number;
    status?: string;
  },
): Promise<{ ok: true; admin: PlatformAdmin } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/admins/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformAdmin>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, admin: out.data };
}

export async function deletePlatformAdmin(
  id: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/admins/${id}`, { method: 'DELETE' });
  const out = await parseJsonResponse<null>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true };
}

export async function suspendPlatformAdmin(
  id: number,
): Promise<{ ok: true; admin: PlatformAdmin } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/admins/${id}/suspend`, { method: 'POST' });
  const out = await parseJsonResponse<PlatformAdmin>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, admin: out.data };
}

export async function activatePlatformAdmin(
  id: number,
): Promise<{ ok: true; admin: PlatformAdmin } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/admins/${id}/activate`, { method: 'POST' });
  const out = await parseJsonResponse<PlatformAdmin>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, admin: out.data };
}
